import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import Chart from "react-apexcharts";
import { useNavigate } from "react-router-dom";

import ToastButton from "../../../Components/ExtraComponents/Alert_Toast";
import Loader from "../../../Utils/Loader";

import { useDispatch } from "react-redux";
import { Get_Dashboard_Count } from "../../../ReduxStore/Slice/Admin/DashboardSlice";

const Dashboard = ({ data }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Memoize user token
  const userToken = useMemo(() => {
    return JSON.parse(localStorage.getItem("user_details"))?.token;
  }, []);

  const [dashboardData, setDashboardData] = useState({
    loading: true,
    data: {},
    error: null,
  });

  // 🔹 activeView state
  const [activeView, setActiveView] = useState("cards"); // default safe value

  // 🔹 Load view based on localStorage or theme
  useEffect(() => {
    const saved = localStorage.getItem("activeView");
    if (saved) {
      setActiveView(saved);
      return;
    }

    const bodyClass = document.body.className;
    let view = "cards"; // fallback default

    if (bodyClass.includes("ocean-blue")) view = "compactcards";
    else if (bodyClass.includes("teal-corporate")) view = "compactcards";
    else if (bodyClass.includes("emerald-nature")) view = "compactcards";
    else if (bodyClass.includes("sunset-warm")) view = "cards";
    else if (bodyClass.includes("dark")) view = "cards";
    else if (bodyClass.includes("light")) view = "cards";
    

    setActiveView(view);
    localStorage.setItem("activeView", view);
  }, []);

  // 🔹 Fetch Dashboard Data
  const fetchDashboardData = useCallback(async () => {
    if (!userToken) {
      setDashboardData((prev) => ({
        ...prev,
        loading: false,
        error: "No authentication token found",
      }));
      return;
    }

    try {
      setDashboardData((prev) => ({ ...prev, loading: true, error: null }));

      const response = await dispatch(Get_Dashboard_Count(userToken)).unwrap();

      if (response.status) {
        setDashboardData({
          loading: false,
          data: response.totalCount || {},
          error: null,
        });
      } else {
        setDashboardData({
          loading: false,
          data: {},
          error: "Failed to fetch dashboard data",
        });
      }
    } catch (error) {
      setDashboardData({
        loading: false,
        data: {},
        error: error.message || "Failed to fetch dashboard data",
      });
    }
  }, [dispatch, userToken]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Chart options
  const getChartOptions = useMemo(
    () => (color) => ({
      chart: { type: "area", sparkline: { enabled: true } },
      stroke: { curve: "smooth", width: 2 },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.6,
          opacityTo: 0.05,
          stops: [0, 90, 100],
        },
      },
      tooltip: { enabled: false },
      colors: [color || "#00BFFF"],
    }),
    []
  );

  const chartSeries = useMemo(
    () => [{ name: "series-1", data: [10, 20, 15, 30, 25, 35, 28] }],
    []
  );

  const cardConfig = useMemo(
    () => [
      {
        index: 1,
        name: "Total Client",
        key: "total_client",
        route: "/admin/allclients",
        visible: true,
        color: "#42A5F5",
      },
      {
        index: 2,
        name: "Active Client",
        key: "total_active_client",
        route: "/admin/allclients?filter=111",
        visible: true,
        color: "#66BB6A",
      },
      {
        index: 3,
        name: "Expired Client",
        key: "total_expired_client",
        route: "/admin/allclients?filter=110",
        visible: true,
        color: "#EF5350",
      },
      {
        index: 4,
        name: "Total Live Client",
        key: "total_live_client",
        route: "/admin/allclients?filter=2",
        visible: true,
        color: "#AB47BC",
      },
      {
        index: 5,
        name: "Active Live Client",
        key: "total_active_live",
        route: "/admin/allclients?filter=21",
        visible: true,
        color: "#66BB6A",
      },
      {
        index: 6,
        name: "Expired Live Client",
        key: "total_expired_live",
        route: "/admin/allclients?filter=20",
        visible: true,
        color: "#FFA726",
      },
      {
        index: 7,
        name: "Total Demo Client",
        key: "total_demo_client",
        route: "/admin/allclients?filter=1",
        visible: true,
        color: "#AB47BC",
      },
      {
        index: 8,
        name: "Active Demo Client",
        key: "total_active_demo",
        route: "/admin/allclients?filter=11",
        visible: true,
        color: "#66BB6A",
      },
      {
        index: 9,
        name: "Expired Demo Client",
        key: "total_expired_demo",
        route: "/admin/allclients?filter=10",
        visible: true,
        color: "#FFA726",
      },
      {
        index: 10,
        name: "Total 2 Days Client",
        key: "total_two_days",
        route: "/admin/allclients?filter=0",
        visible: true,
        color: "#AB47BC",
      },
      {
        index: 11,
        name: "Active 2 Days Client",
        key: "total_active_two_days",
        route: "/admin/allclients?filter=01",
        visible: true,
        color: "#66BB6A",
      },
      {
        index: 12,
        name: "Expired 2 Days Client",
        key: "total_expired_two_days",
        route: "/admin/allclients?filter=00",
        visible: true,
        color: "#FFA726",
      },
      {
        index: 13,
        name: "Total License",
        key: "all_licence",
        route: "/admin/allLicence?filter=0",
        visible: true,
        color: "#AB47BC",
      },
      {
        index: 14,
        name: "Remaining License",
        key: "remaining_licence",
        route: "/admin/allLicence",
        visible: true,
        color: "#66BB6A",
      },
      {
        index: 15,
        name: "Used License",
        key: "used_licence",
        route: "/admin/allLicence?filter=1",
        visible: true,
        color: "#FFB74D",
      },

    ],
    []
  );

  const sortedCards = useMemo(() => {
    const customOrder = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
    return cardConfig
      .map((card) => ({
        ...card,
        value: dashboardData.data[card.key] ?? 0,
      }))
      .sort(
        (a, b) => customOrder.indexOf(a.index) - customOrder.indexOf(b.index)
      );
  }, [cardConfig, dashboardData.data]);

  // Error state
  if (dashboardData.error) {
    return (
      <div className="content-body">
        <div className="container-fluid">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Error Loading Dashboard</h4>
            <p>{dashboardData.error}</p>
            <button
              className="btn btn-outline-danger"
              onClick={fetchDashboardData}
            >
              Retry
            </button>
          </div>
        </div>
        <ToastButton />
      </div>
    );
  }

  return (
    <>
      <div className="content-body">
        <div className="container-fluid">
          <ol className="breadcrumb mb-5">
            <div className="col-lg-6">
              <li className="breadcrumb-item">
                <h4 className="font-w500 mb-2 fs-2">Performance Board</h4>
                <h6 className="font-w500 mb-0">Platform Overview</h6>
              </li>
            </div>
          </ol>

          {/* 🔹 Conditional Rendering based on activeView */}
          {activeView === "compactcards" && (
            <div className="compact-dashboard">
              {sortedCards.map((item) => (
                <div
                  key={item.index}
                  className="compact-item bg-white"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    if (item.visible) navigate(item.route); // using useNavigate from react-router
                  }}
                >
                  <div className="compact-content">
                    <div className="compact-icon">
                      <i className="fa fa-users" />
                    </div>
                    <div className="compact-info">
                      <h4 className="compact-title">{item.name}</h4>
                      <div className="compact-value">
                        {item.value.toLocaleString()}
                      </div>
                    </div>

                    <div className="d-flex flex-column">
                      {item.visible && (
                        <Link to={item.route} className="compact-link">
                          →
                        </Link>
                      )}
                      <div className="compact-chart">
                        <Chart
                          options={getChartOptions(item.color)}
                          series={chartSeries}
                          type="area"
                          height={40}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeView === "cards" && (
            <div className="row ">
              {sortedCards.map((item) => (
                <div
                  key={item.index}
                  className="col-xl-4 col-lg-4 col-sm-6 col-6 mb-2 mb-lg-4 px-1"
                >
                  {item.visible && (
                    <Link
                      to={item.route}
                      className="small text-primary text-decoration-none"
                    >
                      <div className="card card-dashboard h-100 border">
                        <div className="card-body pb-0 pb-lg-2">
                          <div className="row justify-content-between align-items-center">
                            <div className="col-8 pe-0 ps-1 ps-lg-3">
                              <h4 className="mb-1 text-truncate h5">{item.name}</h4>



                            </div>
                            <div className="col-4 text-end ps-0">
                              <h3 className="mb-0 fw-bold text-primary">
                                {item.value.toLocaleString()}
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="col-12">
                          <Chart
                            className="chart-div"
                            options={getChartOptions(item.color)}
                            series={chartSeries}
                            type="area"
                            height={80}
                          />
                        </div>
                      </div>
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ToastButton />
    </>
  );
};

export default Dashboard;
